<template>
  <v-dialog :model-value="modelValue" max-width="850" @update:modelValue="$emit('update:modelValue', $event)" persistent>
    <v-card class="forum-card overflow-hidden">
      <v-row no-gutters class="fill-height">
        <!-- Left Pane: Image & Animated Hook -->
        <v-col cols="12" md="6" class="login-left-pane d-none d-md-flex flex-column justify-end pa-8 position-relative">
          <div class="login-overlay"></div>
          <div class="login-hook-content z-index-10 position-relative">
            <h2 class="text-h3 font-weight-black text-white mb-2 hook-title">
              Built for<br>
              <div class="roller-container text-primary">
                <div class="roller-text">
                  <div class="roller-item">programmers</div>
                  <div class="roller-item">composers</div>
                  <div class="roller-item">artists</div>
                  <div class="roller-item">gamers</div>
                  <div class="roller-item">marketers</div>
                  <!-- Duplicate first item to create continuous loop -->
                  <div class="roller-item">programmers</div>
                </div>
              </div>
            </h2>
            <p class="text-body-1 text-white text-opacity-80 font-weight-medium">Connect, share your journey, and discover amazing projects on the AT Protocol.</p>
          </div>
        </v-col>

        <!-- Right Pane: Login Action -->
        <v-col cols="12" md="6" class="d-flex flex-column bg-grey-darken-4">
          <div class="login-right-content pa-8 pa-sm-10 d-flex flex-column justify-center flex-grow-1">
            
            <div class="d-flex justify-space-between align-start mb-8">
              <v-icon icon="mdi-gamepad-variant-outline" size="48" color="primary" class="mb-4"></v-icon>
              <v-btn icon="mdi-close" variant="text" size="small" color="grey" @click="$emit('update:modelValue', false)" :disabled="loading"></v-btn>
            </div>

            <h2 class="text-h4 font-weight-black text-white mb-2">Halo, Om!</h2>
            <p class="text-grey mb-8">Sign in to join the #gamedevid community</p>

            <v-form @submit.prevent="handleLogin" ref="form">
              <v-alert
                v-if="error"
                type="error"
                variant="tonal"
                class="mb-6 rounded-lg"
                density="compact"
              >
                {{ error }}
              </v-alert>
              
              <v-btn
                block
                color="primary"
                size="large"
                type="submit"
                :loading="loading"
                class="login-btn font-weight-bold text-none rounded-pill"
                elevation="2"
              >
                Login with Bluesky
              </v-btn>
            </v-form>

            <div class="forum-footer d-flex flex-column align-center mt-6 pt-6 border-t border-white-05">
              <p class="text-caption text-grey">New to the community?</p>
              <v-btn variant="text" color="primary" class="font-weight-bold text-none mt-1" href="https://bsky.app" target="_blank">
                Join Bluesky Today
              </v-btn>
            </div>

            
          </div>
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { login } from '@/services/bluesky';

const props = defineProps({
  modelValue: Boolean
});

const emit = defineEmits(['update:modelValue', 'login-success']);

const loading = ref(false);
const error = ref('');

const handleLogin = async () => {
  loading.value = true;
  error.value = '';
  
  try {
    // This will redirect to Bluesky OAuth
    await login('https://bsky.social');
    // Note: The UI won't reach here if redirect succeeds
    emit('update:modelValue', false);
  } catch (err: any) {
    error.value = err.message || 'Login failed. Please check your Handle.';
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.forum-card {
  border-radius: 24px !important;
  background-color: #1a1a1a !important;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 40px 80px -20px rgba(0, 0, 0, 0.7) !important;
  min-height: 500px;
}

.login-left-pane {
  background-image: url('/login_photo.jpg');
  background-size: cover;
  background-position-x: -300px;
}

.login-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.4) 50%, rgba(0, 0, 0, 0.1) 100%);
}

.z-index-10 {
  z-index: 10;
}

.hook-title {
  line-height: 1.1;
  letter-spacing: -0.02em;
}

/* Sliding Text Roller Animation */
.roller-container {
  height: 1.2em; /* Viewport height for exactly one item */
  overflow: hidden;
  position: relative;
  display: inline-block;
  vertical-align: bottom;
  min-width: 250px;
}

.roller-text {
  animation: rollUp 10s cubic-bezier(0.85, 0, 0.15, 1) infinite;
}

.roller-item {
  height: 1.2em;
  display: flex;
  align-items: flex-end;
}

/* 5 items + 1 duplicate = 6 stops */
@keyframes rollUp {
  0%, 15%   { transform: translateY(0); }
  20%, 35%  { transform: translateY(-1.2em); }
  40%, 55%  { transform: translateY(-2.4em); }
  60%, 75%  { transform: translateY(-3.6em); }
  80%, 95%  { transform: translateY(-4.8em); }
  100%      { transform: translateY(-6.0em); }
}

.login-btn {
  height: 56px !important;
  letter-spacing: 0.5px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.login-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px -5px rgba(var(--v-theme-primary), 0.4) !important;
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}

.text-tiny {
  font-size: 0.7rem;
  line-height: 1.2;
}

.tech-badge {
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.5);
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.feature-mini:hover .v-icon {
  color: var(--v-theme-primary) !important;
  transform: scale(1.1);
}

.feature-mini .v-icon {
  transition: all 0.3s ease;
}
</style>
